<div class="min-h-screen bg-gray-50 p-6 print:p-2 print:bg-white">
  <!-- Error State -->
  <div *ngIf="error" class="max-w-4xl mx-auto">
    <nz-alert nzType="error" [nzMessage]="error" nzShowIcon></nz-alert>
  </div>

  <!-- Main Content -->
  <div *ngIf="traceData" class="max-w-7xl mx-auto space-y-6">
    <!-- Header -->
    <div
      class="bg-white rounded-xl shadow-sm border p-6 flex flex-wrap items-center justify-between gap-4 print:shadow-none print:border-b print:rounded-none"
    >
      <div>
        <h1 class="text-2xl font-bold text-gray-800">Score Breakdown Trace</h1>
        <p class="text-gray-500 mt-1">
          Detailed view of how the disease risk score was calculated
        </p>
      </div>
      <div class="flex items-center gap-3 print:hidden">
        <button nz-button nzType="default" (click)="printPage()">
          <span nz-icon nzType="printer" nzTheme="outline"></span>
          Print
        </button>
      </div>
    </div>

    <!-- Summary Cards -->
    <div class="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
      <div
        *ngIf="traceData.scenarioId"
        class="bg-white rounded-xl shadow-sm border p-4 print:shadow-none"
      >
        <div class="text-xs text-gray-500 uppercase tracking-wide mb-1">
          Scenario ID
        </div>
        <div
          class="font-mono text-sm text-gray-700 truncate"
          [title]="traceData.scenarioId"
        >
          {{ traceData.scenarioId }}
        </div>
      </div>
      <div
        *ngIf="traceData.diseaseName"
        class="bg-white rounded-xl shadow-sm border p-4 print:shadow-none"
      >
        <div class="text-xs text-gray-500 uppercase tracking-wide mb-1">
          Disease
        </div>
        <div class="font-semibold text-gray-700">
          {{ traceData.diseaseName }}
        </div>
      </div>
      <div
        class="bg-purple-50 border border-purple-200 rounded-xl p-4 text-center"
      >
        <div class="text-xs text-purple-600 uppercase tracking-wide mb-1">
          DRS Score
        </div>
        <div class="text-3xl font-bold text-purple-700">
          {{ traceData.drsScore ?? "—" }}
        </div>
      </div>
      <div
        class="bg-orange-50 border border-orange-200 rounded-xl p-4 text-center"
      >
        <div class="text-xs text-orange-600 uppercase tracking-wide mb-1">
          PDH Score
        </div>
        <div class="text-3xl font-bold text-orange-700">
          {{ traceData.pdhScore ?? "—" }}
        </div>
      </div>
      <div
        class="bg-blue-50 border border-blue-200 rounded-xl p-4 text-center"
        *ngIf="traceData.band"
      >
        <div class="text-xs text-blue-600 uppercase tracking-wide mb-1">
          Band
        </div>
        <div class="text-xl font-bold text-blue-700">{{ traceData.band }}</div>
      </div>
    </div>

    <!-- Points Summary -->
    <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
      <div
        class="bg-purple-100 border border-purple-300 rounded-xl p-5 text-center"
      >
        <div class="text-sm text-purple-700 font-medium mb-2">
          Questionnaire Raw Points
        </div>
        <div class="text-4xl font-bold text-purple-800">
          {{ traceData.trace.totals.rawQuestionnairePoints }}
        </div>
      </div>
      <div
        class="bg-purple-50 border border-purple-200 rounded-xl p-5 text-center"
      >
        <div class="text-sm text-purple-600 font-medium mb-2">
          Questionnaire Max Points
        </div>
        <div class="text-4xl font-bold text-purple-700">
          {{ traceData.trace.totals.maxQuestionnairePoints }}
        </div>
      </div>
      <div
        class="bg-green-100 border border-green-300 rounded-xl p-5 text-center"
      >
        <div class="text-sm text-green-700 font-medium mb-2">
          Lab Raw Points
        </div>
        <div class="text-4xl font-bold text-green-800">
          {{ traceData.trace.totals.rawLabPoints }}
        </div>
      </div>
      <div
        class="bg-green-50 border border-green-200 rounded-xl p-5 text-center"
      >
        <div class="text-sm text-green-600 font-medium mb-2">
          Lab Max Points
        </div>
        <div class="text-4xl font-bold text-green-700">
          {{ traceData.trace.totals.maxLabPoints }}
        </div>
      </div>
    </div>

    <!-- Scenario Selection -->
    <div
      class="bg-white rounded-xl shadow-sm border overflow-hidden print:shadow-none"
      *ngIf="traceData.trace.scenarioTrace"
    >
      <div class="bg-blue-600 text-white px-6 py-4">
        <h2 class="text-lg font-semibold flex items-center gap-2">
          <span nz-icon nzType="branches" nzTheme="outline"></span>
          Scenario Selection
        </h2>
      </div>
      <div class="p-6">
        <!-- Selected Scenario Highlight -->
        <div class="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div class="flex flex-wrap items-center gap-6">
            <div>
              <span class="text-sm text-gray-600">Selected Scenario:</span>
              <span class="ml-2 text-lg font-bold text-blue-700">{{
                traceData.trace.scenarioTrace.selectedScenario
              }}</span>
            </div>
            <div class="flex items-center gap-4">
              <div class="px-4 py-2 bg-purple-100 rounded-lg">
                <span class="text-sm text-purple-600"
                  >Questionnaire Weight (wQ):</span
                >
                <span
                  class="ml-2 text-xl font-mono font-bold text-purple-700"
                  >{{ traceData.trace.scenarioTrace.weights.wQ }}</span
                >
              </div>
              <div class="px-4 py-2 bg-green-100 rounded-lg">
                <span class="text-sm text-green-600">Lab Weight (wL):</span>
                <span class="ml-2 text-xl font-mono font-bold text-green-700">{{
                  traceData.trace.scenarioTrace.weights.wL
                }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Filter Controls -->
        <div class="flex items-center gap-4 mb-4">
          <span class="text-sm text-gray-600">Filter:</span>
          <nz-radio-group
            [(ngModel)]="scenarioMatchedFilter"
            (ngModelChange)="onScenarioFilterChange($event)"
            nzButtonStyle="solid"
            nzSize="small"
          >
            <label nz-radio-button nzValue="all">All</label>
            <label nz-radio-button nzValue="matched">Matched</label>
            <label nz-radio-button nzValue="notMatched">Not Matched</label>
          </nz-radio-group>
          <span class="text-xs text-gray-500 ml-auto">
            Showing {{ filteredScenarios.length }} of
            {{ traceData.trace.scenarioTrace.evaluatedScenarios.length }}
          </span>
        </div>

        <!-- Scenarios Table -->
        <nz-table
          [nzData]="filteredScenarios"
          [nzShowPagination]="false"
          nzSize="middle"
          [nzBordered]="true"
        >
          <thead>
            <tr>
              <th nzWidth="30%">Scenario Name</th>
              <th>Condition</th>
              <th nzWidth="120px" class="text-center">Matched</th>
            </tr>
          </thead>
          <tbody>
            <tr
              *ngFor="let s of filteredScenarios"
              [class.bg-green-50]="s.matched"
            >
              <td class="font-medium">{{ s.scenarioName }}</td>
              <td class="font-mono text-sm text-gray-600">{{ s.condition }}</td>
              <td class="text-center">
                <nz-tag
                  [nzColor]="s.matched ? 'green' : 'default'"
                  class="text-sm px-3"
                >
                  {{ s.matched ? "✓ Yes" : "No" }}
                </nz-tag>
              </td>
            </tr>
          </tbody>
        </nz-table>
      </div>
    </div>

    <!-- Questionnaire Breakdown -->
    <div
      class="bg-white rounded-xl shadow-sm border overflow-hidden print:shadow-none"
      *ngIf="traceData.trace.questionnaireBreakdown?.length"
    >
      <div
        class="bg-purple-600 text-white px-6 py-4 flex items-center justify-between"
      >
        <h2 class="text-lg font-semibold flex items-center gap-2">
          <span nz-icon nzType="form" nzTheme="outline"></span>
          Questionnaire Breakdown
        </h2>
        <div class="flex items-center gap-4 text-sm">
          <span
            >Matched:
            <strong>{{
              getMatchedCount(traceData.trace.questionnaireBreakdown)
            }}</strong>
            / {{ traceData.trace.questionnaireBreakdown.length }}</span
          >
          <span
            >Points:
            <strong>{{
              getTotalPoints(traceData.trace.questionnaireBreakdown)
            }}</strong></span
          >
        </div>
      </div>
      <div class="p-6">
        <!-- Filter Controls -->
        <div class="flex flex-wrap items-center gap-4 mb-4">
          <span class="text-sm text-gray-600">Filter:</span>
          <nz-radio-group
            [(ngModel)]="questionnaireMatchedFilter"
            (ngModelChange)="onQuestionnaireFilterChange($event)"
            nzButtonStyle="solid"
            nzSize="small"
          >
            <label nz-radio-button nzValue="all">All</label>
            <label nz-radio-button nzValue="matched">Matched</label>
            <label nz-radio-button nzValue="notMatched">Not Matched</label>
          </nz-radio-group>
          <span class="text-xs text-gray-500 ml-auto">
            Showing {{ filteredQuestionnaire.length }} of
            {{ traceData.trace.questionnaireBreakdown.length }}
          </span>
        </div>

        <nz-table
          [nzData]="filteredQuestionnaire"
          [nzShowPagination]="false"
          nzSize="middle"
          [nzBordered]="true"
        >
          <thead>
            <tr>
              <th nzWidth="180px">Fact Key</th>
              <th nzWidth="180px">Input Value</th>
              <th>Evaluated Condition</th>
              <th nzWidth="120px" class="text-center">Matched</th>
              <th
                nzWidth="100px"
                class="text-center"
                [nzSortOrder]="questionnairePointsSort"
                (nzSortOrderChange)="onQuestionnairePointsSort($event)"
                [nzSortFn]="true"
              >
                Points
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              *ngFor="let q of filteredQuestionnaire"
              [class.bg-green-50]="q.matched"
            >
              <td class="font-mono font-medium">{{ q.factKey }}</td>
              <td class="font-mono">{{ q.inputValue }}</td>
              <td class="font-mono text-sm text-gray-600">
                {{ q.evaluatedCondition }}
              </td>
              <td class="text-center">
                <nz-tag
                  [nzColor]="q.matched ? 'green' : 'default'"
                  class="text-sm px-3"
                >
                  {{ q.matched ? "✓ Yes" : "No" }}
                </nz-tag>
              </td>
              <td
                class="text-center font-mono text-lg font-bold"
                [class.text-green-600]="q.pointsAwarded > 0"
              >
                {{ q.pointsAwarded }}
              </td>
            </tr>
          </tbody>
        </nz-table>
      </div>
    </div>

    <!-- Lab Breakdown -->
    <div
      class="bg-white rounded-xl shadow-sm border overflow-hidden print:shadow-none"
      *ngIf="traceData.trace.labBreakdown?.length"
    >
      <div
        class="bg-green-600 text-white px-6 py-4 flex items-center justify-between"
      >
        <h2 class="text-lg font-semibold flex items-center gap-2">
          <span nz-icon nzType="experiment" nzTheme="outline"></span>
          Lab Breakdown
        </h2>
        <div class="flex items-center gap-4 text-sm">
          <span
            >Available:
            <strong>{{
              getAvailableCount(traceData.trace.labBreakdown)
            }}</strong>
            / {{ traceData.trace.labBreakdown.length }}</span
          >
          <span
            >Points:
            <strong>{{
              getTotalPoints(traceData.trace.labBreakdown)
            }}</strong></span
          >
        </div>
      </div>
      <div class="p-6">
        <!-- Filter Controls -->
        <div class="flex flex-wrap items-center gap-4 mb-4">
          <span class="text-sm text-gray-600">Filter:</span>
          <nz-radio-group
            [(ngModel)]="labMatchedFilter"
            (ngModelChange)="onLabFilterChange($event)"
            nzButtonStyle="solid"
            nzSize="small"
          >
            <label nz-radio-button nzValue="all">All</label>
            <label nz-radio-button nzValue="matched">Matched</label>
            <label nz-radio-button nzValue="notMatched">Not Matched</label>
          </nz-radio-group>
          <span class="text-xs text-gray-500 ml-auto">
            Showing {{ filteredLab.length }} of
            {{ traceData.trace.labBreakdown.length }}
          </span>
        </div>

        <nz-table
          [nzData]="filteredLab"
          [nzShowPagination]="false"
          nzSize="middle"
          [nzBordered]="true"
        >
          <thead>
            <tr>
              <th nzWidth="150px">Marker Key</th>
              <th nzWidth="100px" class="text-center">Value</th>
              <th nzWidth="110px" class="text-center">Available</th>
              <th>Evaluated Condition</th>
              <th nzWidth="110px" class="text-center">Matched</th>
              <th
                nzWidth="100px"
                class="text-center"
                [nzSortOrder]="labPointsSort"
                (nzSortOrderChange)="onLabPointsSort($event)"
                [nzSortFn]="true"
              >
                Points
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              *ngFor="let l of filteredLab"
              [class.bg-green-50]="l.matched"
              [class.bg-gray-100]="!l.available"
            >
              <td class="font-mono font-medium">{{ l.markerKey }}</td>
              <td class="text-center font-mono">
                {{ l.inputValue !== null ? l.inputValue : "—" }}
              </td>
              <td class="text-center">
                <nz-tag
                  [nzColor]="l.available ? 'blue' : 'default'"
                  class="text-sm px-3"
                >
                  {{ l.available ? "✓ Yes" : "No" }}
                </nz-tag>
              </td>
              <td class="font-mono text-sm text-gray-600">
                {{ l.evaluatedCondition }}
              </td>
              <td class="text-center">
                <nz-tag
                  [nzColor]="l.matched ? 'green' : 'default'"
                  class="text-sm px-3"
                >
                  {{ l.matched ? "✓ Yes" : "No" }}
                </nz-tag>
              </td>
              <td
                class="text-center font-mono text-lg font-bold"
                [class.text-green-600]="l.pointsAwarded > 0"
              >
                {{ l.pointsAwarded }}
              </td>
            </tr>
          </tbody>
        </nz-table>
      </div>
    </div>

    <!-- Input Data (Collapsible) -->
    <nz-collapse class="print:hidden">
      <nz-collapse-panel nzHeader="Input Data (Raw)" [nzActive]="false">
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <!-- Questionnaire Facts -->
          <div *ngIf="traceData.questionnaireFacts">
            <h4 class="text-sm font-semibold text-gray-700 mb-3">
              Questionnaire Facts
            </h4>
            <div class="bg-gray-50 rounded-lg p-4 max-h-80 overflow-auto">
              <pre class="text-xs font-mono whitespace-pre-wrap">{{
                traceData.questionnaireFacts | json
              }}</pre>
            </div>
          </div>
          <!-- Lab Markers -->
          <div *ngIf="traceData.labMarkers">
            <h4 class="text-sm font-semibold text-gray-700 mb-3">
              Lab Markers
            </h4>
            <div class="bg-gray-50 rounded-lg p-4 max-h-80 overflow-auto">
              <pre class="text-xs font-mono whitespace-pre-wrap">{{
                traceData.labMarkers | json
              }}</pre>
            </div>
          </div>
        </div>
      </nz-collapse-panel>
    </nz-collapse>
  </div>
</div>

